import {
	createProviderWithCustomFetchData,
	type ExportedDataProvider,
} from './create-data-provider';
import { type AvailableProductsResponse } from '../../types';
import { fetchJsonWithNetworkRetries } from '../utils/fetch';
import { ExperienceMark } from '../utils/experience-tracker';
import { ASSETS_APP_KEY } from '../../constants';
import { type ProductKeys3P } from '../constants/3p-product-configs';
import { type ThirdPartyConfigsBootstrap } from '../constants/schemas/3p-config';
import { type ThirdPartyConfigsWithAPIResponse } from '../constants/schemas/3p-config';
import { ThirdPartyConfigs } from '../constants/3p-product-configs';
import { getThirdPartyTypes } from '../constants/products';

export const APP_SWITCHER_BFF_AVAILABLE_PRODUCTS_ENDPOINT =
	'/gateway/api/app-switcher/api/available-products';

const transformAppSwitcherBffResponse = (response: AvailableProductsResponse) => {
	// TODO: remove this once Asset app becomes standalone app
	if (response.switcherTeam25ExperimentCohort === 'test') {
		response.sites.forEach((site) => {
			if (
				site.availableProducts.some(
					(product) => product.productType === 'JIRA_SERVICE_DESK' && product.isJsmPremium,
				)
			) {
				site.availableProducts.push({ productType: ASSETS_APP_KEY });
			}
		});
	}

	//Processing for thirdPartyConfig: Adds config for third party products, filters out products that are not user authed or not a valid product key
	const thirdPartyConfig = response?.thirdPartyConfig;
	if (thirdPartyConfig) {
		const THIRD_PARTY_TYPES: Record<string, string> = getThirdPartyTypes();

		let productKeys = [] as ProductKeys3P[];
		let thirdPartyConfigsWithAPIResponse: ThirdPartyConfigsBootstrap = {};

		Object.values(thirdPartyConfig).forEach((thirdPartyItem: any) => {
			const productKey = (THIRD_PARTY_TYPES[thirdPartyItem.type] || '') as ProductKeys3P;

			if (!productKey || !thirdPartyItem.isUserOAuthed) {
				return;
			}

			productKeys.push(productKey);
			let config = {} as ThirdPartyConfigsWithAPIResponse;
			const staticConfig = ThirdPartyConfigs.get(productKey);

			if (staticConfig) {
				config = {
					...staticConfig,
					outboundAuthUrl: thirdPartyItem.outboundAuthUrl,
					userNeedsOAuth: !thirdPartyItem.isUserOAuthed,
					providerId: thirdPartyItem.providerId,
					workspaceName: thirdPartyItem.workspaceName,
					workspaceUrl: thirdPartyItem.workspaceUrl,
				};

				thirdPartyConfigsWithAPIResponse[productKey] = config;
			}
		});
		response.thirdPartyConfig = thirdPartyConfigsWithAPIResponse;
	}

	return response;
};

interface Params {
	cloudId?: string;
	product?: string;
}

export const appSwitcherBffAvailableProductsFetch = async ({ cloudId, product }: Params) => {
	const params = new URLSearchParams({ source: 'app-switcher-fe' });
	if (cloudId) {
		params.append('tenantid', encodeURIComponent(cloudId));
	}
	if (product) {
		params.append('product', encodeURIComponent(product));
	}

	const response = await fetchJsonWithNetworkRetries<AvailableProductsResponse>(
		`${APP_SWITCHER_BFF_AVAILABLE_PRODUCTS_ENDPOINT}?${params.toString()}`,
		{
			intervalsMS: [50, 200, 1000],
		},
		{
			headers: {
				'Atl-App-Switcher-Private-API': 'true',
			},
		},
	);

	return transformAppSwitcherBffResponse(response);
};

export const createAppSwitcherBffAvailableProductsProvider =
	(): ExportedDataProvider<AvailableProductsResponse> => {
		return createProviderWithCustomFetchData<AvailableProductsResponse, Params>(
			'appSwitcherBffAvailableProducts',
			ExperienceMark.API_APP_SWITCHER_BFF_AVAILABLE_PRODUCTS,
			appSwitcherBffAvailableProductsFetch,
		);
	};
